import { useEffect, useRef, useState, type FormEvent } from 'react';
import { demoEmail, demoPassword } from '../lib/demo-credentials';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../layouts/AuthLayout';
import { useAuth } from '../auth/AuthContext';
import type { ApiError } from '../api/client';

const REMEMBERED_EMAIL = 'sx.remember.email';

/**
 * Turn an API failure into something a person can act on.
 *
 * Deliberately vague about credentials — "we could not sign you in" for both a
 * wrong password and an unknown address, because saying which one is wrong
 * tells an attacker whether an address exists. Everything else is specific,
 * because a locked account and a network failure need different responses and
 * a single generic message leaves people retrying the wrong thing.
 */
function describe(error: ApiError): { title: string; detail?: string; action?: 'reset' } {
  const code = error.code ?? '';
  const message = error.message ?? '';

  if (error.status === 0) {
    return {
      title: 'We could not reach SkillXzen.',
      detail: 'Check your connection and try again.',
    };
  }
  if (error.status === 429 || code === 'THROTTLED') {
    return {
      title: 'Too many attempts.',
      detail: 'Please wait a minute before trying again.',
    };
  }
  if (code === 'ACCOUNT_LOCKED' || /locked/i.test(message)) {
    return {
      title: 'This account is temporarily locked.',
      detail: 'Too many failed attempts. It unlocks shortly, or you can reset your password.',
      action: 'reset',
    };
  }
  if (code === 'ACCOUNT_DISABLED' || /suspended|disabled|inactive/i.test(message)) {
    return {
      title: 'This account is not active.',
      detail: 'Your administrator can reactivate it.',
    };
  }
  if (code === 'EMAIL_NOT_VERIFIED' || /verif/i.test(message)) {
    return {
      title: 'Confirm your email address first.',
      detail: 'We sent you a link when you registered. We can send another.',
    };
  }
  if (error.status >= 500) {
    return {
      title: 'Sign-in is unavailable right now.',
      detail: 'This is our fault, not yours. Please try again shortly.',
    };
  }
  return {
    title: 'We could not sign you in.',
    detail: 'Please check your credentials and try again.',
    action: 'reset',
  };
}

export function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState(() => {
    try {
      // A remembered address wins over the demonstration one: it is the
      // choice this person actually made on this device.
      return window.localStorage.getItem(REMEMBERED_EMAIL) ?? demoEmail();
    } catch {
      return demoEmail();
    }
  });
  const [remember, setRemember] = useState(() => {
    try {
      return Boolean(window.localStorage.getItem(REMEMBERED_EMAIL));
    } catch {
      return false;
    }
  });
  const [password, setPassword] = useState(demoPassword);
  const [reveal, setReveal] = useState(false);
  const [tenantSlug, setTenantSlug] = useState('');
  const [needsTenant, setNeedsTenant] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // Checked here, never sent: an empty password can only fail, and every
  // failure counts towards the account lockout.
  const [missingPassword, setMissingPassword] = useState(false);
  // With the address already filled, the password box is the next thing to do.
  const [startInPassword] = useState(() => email.trim().length > 0 && password.length === 0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const errorRef = useRef<HTMLDivElement>(null);
  const tenantRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Move attention to the failure. Without this a screen-reader user submits
  // the form and hears nothing change.
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    if (needsTenant) tenantRef.current?.focus();
  }, [needsTenant]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password.length === 0) {
      setMissingPassword(true);
      passwordRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);

    try {
      await signIn(email, password, tenantSlug || undefined);

      try {
        if (remember) window.localStorage.setItem(REMEMBERED_EMAIL, email);
        else window.localStorage.removeItem(REMEMBERED_EMAIL);
      } catch {
        // Private mode: the address simply will not be remembered.
      }

      // A beat of confirmation before the route changes, so the success is
      // visible rather than the page just vanishing.
      setDone(true);
      const to = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      window.setTimeout(() => navigate(to, { replace: true }), 260);
    } catch (caught) {
      const apiError = caught as ApiError;
      // The API asks for an organization when one address exists in several.
      if (apiError.code === 'VALIDATION_ERROR' && /organization/i.test(apiError.message)) {
        setNeedsTenant(true);
      }
      setError(apiError);
      setPassword('');
      setBusy(false);
    }
  }

  const described = error ? describe(error) : null;

  return (
    <AuthLayout>
      <header className="authform__head">
        <h2 className="authform__title">Welcome back</h2>
        <p className="authform__lede">Continue building your AI skills with SkillXzen.</p>
      </header>

      {described ? (
        <div
          className="authform__error"
          role="alert"
          tabIndex={-1}
          ref={errorRef}
        >
          <span className="authform__error-mark" aria-hidden="true">
            !
          </span>
          <span>
            <strong>{described.title}</strong>
            {described.detail ? <span className="authform__error-detail">{described.detail}</span> : null}
          </span>
        </div>
      ) : null}

      <form onSubmit={onSubmit} noValidate className="authform">
        <div className="sxfield">
          <label className="sxfield__label" htmlFor="sx-email">
            Email address
          </label>
          <input
            className="sxfield__input"
            id="sx-email"
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoFocus={!startInPassword}
            required
            aria-invalid={Boolean(error)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="sxfield">
          <div className="sxfield__labelrow">
            <label className="sxfield__label" htmlFor="sx-password">
              Password
            </label>
            <Link className="sxfield__aside" to="/forgot-password">
              Forgot password?
            </Link>
          </div>
          <div className="sxfield__wrap">
            <input
              className="sxfield__input sxfield__input--padded"
              id="sx-password"
              type={reveal ? 'text' : 'password'}
              name="password"
              autoComplete="current-password"
              autoFocus={startInPassword}
              required
              ref={passwordRef}
              aria-invalid={Boolean(error) || missingPassword}
              aria-describedby={missingPassword ? 'sx-password-hint' : undefined}
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                if (event.target.value) setMissingPassword(false);
              }}
            />
            <button
              type="button"
              className="sxfield__reveal"
              onClick={() => setReveal((value) => !value)}
              aria-pressed={reveal}
              aria-label={reveal ? 'Hide password' : 'Show password'}
              title={reveal ? 'Hide password' : 'Show password'}
            >
              {reveal ? 'Hide' : 'Show'}
            </button>
          </div>
          {missingPassword ? (
            <p className="sxfield__hint sxfield__hint--error" id="sx-password-hint" role="alert">
              Enter your password to sign in.
            </p>
          ) : null}
        </div>

        {needsTenant ? (
          <div className="sxfield">
            <label className="sxfield__label" htmlFor="sx-tenant">
              Organization
            </label>
            <input
              className="sxfield__input"
              id="sx-tenant"
              name="tenantSlug"
              placeholder="acme-engineering"
              ref={tenantRef}
              value={tenantSlug}
              onChange={(event) => setTenantSlug(event.target.value)}
              aria-describedby="sx-tenant-hint"
            />
            <p className="sxfield__hint" id="sx-tenant-hint">
              Your email is registered with more than one organization.
            </p>
          </div>
        ) : null}

        <label className="sxcheck">
          <input
            type="checkbox"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
          />
          <span className="sxcheck__box" aria-hidden="true" />
          <span>Remember my email address</span>
        </label>

        <button
          className={`sxbtn${done ? ' sxbtn--done' : ''}`}
          type="submit"
          disabled={busy || done}
        >
          {done ? 'Signed in' : busy ? 'Signing in…' : 'Sign in'}
          {busy && !done ? <span className="sxbtn__spinner" aria-hidden="true" /> : null}
        </button>

        <p className="authform__foot">
          Don’t have an account? <Link to="/register">Create your account</Link>
        </p>

        {/*
          A statement of how the product behaves, not a compliance badge. It
          describes, in plain words, the two controls that are actually
          implemented — argon2id hashing and per-account lockout — and claims
          no certification the platform has not been granted.
        */}
        <p className="authform__assurance">
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M8 1 2.5 3.3v4.2c0 3.4 2.3 6.5 5.5 7.5 3.2-1 5.5-4.1 5.5-7.5V3.3L8 1Zm0 1.6 4 1.7v3.2c0 2.7-1.7 5.2-4 6.1-2.3-.9-4-3.4-4-6.1V4.3l4-1.7Z"
            />
            <path fill="currentColor" d="M7.3 9.2 5.9 7.8l-.9.9 2.3 2.3 4-4-.9-.9-3.1 3.1Z" />
          </svg>
          Your password is stored securely. Too many wrong attempts will lock the account.
        </p>
      </form>
    </AuthLayout>
  );
}
